import { useEffect, useState } from 'react';
import { useDevice } from '../hooks/useDevice';
import type { TapLayout } from '../hooks/useReaderSettings';

interface TapHintOverlayProps {
  onDismiss: () => void;
  layout?: TapLayout;
}

export function TapHintOverlay({ onDismiss, layout = 'leftRight-B' }: TapHintOverlayProps) {
  const [isVisible, setIsVisible] = useState(false);
  const { isDesktop } = useDevice();

  useEffect(() => {
    // 延遲顯示動畫
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(onDismiss, 300);
  };

  // 根據佈局渲染示意圖
  const renderLayoutDiagram = () => {
    if (layout === 'topBottom') {
      return (
        <div className="flex flex-col h-24 rounded overflow-hidden text-xs">
          <div className="flex-1 bg-primary-200 flex items-center justify-center text-primary-700 font-medium">
            上一頁
          </div>
          <div className="flex-[1.3] bg-gray-200 flex items-center justify-center text-gray-600 font-medium border-y border-gray-300">
            選單
          </div>
          <div className="flex-1 bg-primary-200 flex items-center justify-center text-primary-700 font-medium">
            下一頁
          </div>
        </div>
      );
    }

    // leftRight-A 或 leftRight-B
    const isReversed = layout === 'leftRight-A';
    return (
      <div className="flex h-20 rounded overflow-hidden text-xs">
        <div className="flex-1 bg-primary-200 flex items-center justify-center text-primary-700 font-medium">
          {isReversed ? '下一頁' : '上一頁'}
        </div>
        <div className="flex-[1.3] bg-gray-200 flex items-center justify-center text-gray-600 font-medium border-x border-gray-300">
          選單
        </div>
        <div className="flex-1 bg-primary-200 flex items-center justify-center text-primary-700 font-medium">
          {isReversed ? '上一頁' : '下一頁'}
        </div>
      </div>
    );
  };

  // 根據佈局生成操作說明文字
  const getTapDescription = () => {
    switch (layout) {
      case 'leftRight-A':
        return '左側下一頁，右側上一頁';
      case 'topBottom':
        return '上方上一頁，下方下一頁';
      default:
        return '左側上一頁，右側下一頁';
    }
  };

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center transition-opacity duration-300 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.85)' }}
      onClick={handleDismiss}
    >
      <div
        className={`max-w-sm mx-4 p-6 rounded-2xl transition-all duration-300 ${
          isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-semibold text-gray-800 mb-4 text-center">
          閱讀操作指南
        </h2>

        <div className="space-y-4 mb-6">
          {/* 滑動閱讀 */}
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
              <svg
                className="w-5 h-5 text-primary-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                />
              </svg>
            </div>
            <div>
              <p className="font-medium text-gray-800">上下滑動</p>
              <p className="text-sm text-gray-500">自由瀏覽內容</p>
            </div>
          </div>

          {/* 點擊快跳 */}
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
              <svg
                className="w-5 h-5 text-primary-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
                />
              </svg>
            </div>
            <div>
              <p className="font-medium text-gray-800">
                點擊螢幕{layout === 'topBottom' ? '上下' : '兩側'}
              </p>
              <p className="text-sm text-gray-500">{getTapDescription()}</p>
            </div>
          </div>

          {/* 開啟選單 */}
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
              <svg
                className="w-5 h-5 text-primary-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </div>
            <div>
              <p className="font-medium text-gray-800">點擊螢幕中央</p>
              <p className="text-sm text-gray-500">開啟設定與導航</p>
            </div>
          </div>
        </div>

        {/* 視覺示意圖 */}
        <div className="mb-6 p-3 rounded-lg bg-gray-100">
          {renderLayoutDiagram()}
        </div>

        {/* 鍵盤快捷鍵 (桌面版) */}
        {isDesktop && (
          <div className="mb-6 border-t pt-4" style={{ borderColor: 'rgba(0,0,0,0.1)' }}>
            <h3 className="text-sm font-medium text-gray-500 mb-3">鍵盤快捷鍵</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex items-center gap-2">
                <kbd className="px-2 py-1 bg-gray-100 border border-gray-300 rounded text-xs font-mono text-gray-700">
                  Space
                </kbd>
                <span className="text-gray-600">下一頁</span>
              </div>
              <div className="flex items-center gap-2">
                <kbd className="px-2 py-1 bg-gray-100 border border-gray-300 rounded text-xs font-mono text-gray-700">
                  PageUp
                </kbd>
                <span className="text-gray-600">上一頁</span>
              </div>
              <div className="flex items-center gap-2">
                <kbd className="px-2 py-1 bg-gray-100 border border-gray-300 rounded text-xs font-mono text-gray-700">
                  ←
                </kbd>
                <span className="text-gray-600">上一章</span>
              </div>
              <div className="flex items-center gap-2">
                <kbd className="px-2 py-1 bg-gray-100 border border-gray-300 rounded text-xs font-mono text-gray-700">
                  →
                </kbd>
                <span className="text-gray-600">下一章</span>
              </div>
              <div className="flex items-center gap-2">
                <kbd className="px-2 py-1 bg-gray-100 border border-gray-300 rounded text-xs font-mono text-gray-700">
                  F
                </kbd>
                <span className="text-gray-600">全螢幕閱讀</span>
              </div>
              <div className="flex items-center gap-2">
                <kbd className="px-2 py-1 bg-gray-100 border border-gray-300 rounded text-xs font-mono text-gray-700">
                  Esc
                </kbd>
                <span className="text-gray-600">顯示介面</span>
              </div>
            </div>
          </div>
        )}

        <button
          onClick={handleDismiss}
          className="w-full py-3 px-4 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 active:bg-primary-800 transition-colors"
        >
          開始閱讀
        </button>
      </div>
    </div>
  );
}
